<template>
  <div class="page-container">
    <v-snackbar v-model="showSnackBar" top right :timeout="4000">
      {{ msg }}
      <template v-slot:action="{ attrs }">
        <v-btn icon small v-bind="attrs" @click="showSnackBar = false">
          <v-icon small color="white">mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>

    <PageHeader :title="isAdmin ? 'All Applications' : 'Division Applications'" :subtitle="subtitle" icon="mdi-file-document-multiple-outline" :count="totalData">
      <template v-slot:actions>
        <v-btn depressed class="btn-act btn-act--neutral" @click="refresh">
          <v-icon left small>mdi-refresh</v-icon>
          Refresh
        </v-btn>
      </template>
    </PageHeader>

    <!-- Status tabs -->
    <div class="status-tabs" role="tablist" aria-label="Application status">
      <button
        v-for="t in tabs"
        :key="t.key"
        type="button"
        role="tab"
        class="status-tab"
        :class="{ 'status-tab--active': t.key === tab }"
        :style="{ '--tone': t.color, '--tone-soft': t.soft }"
        :aria-selected="t.key === tab ? 'true' : 'false'"
        @click="selectTab(t.key)"
      >
        <span class="status-tab-icon"><v-icon small>{{ t.icon }}</v-icon></span>
        <span class="status-tab-text">
          <span class="status-tab-name">{{ t.name }}</span>
          <span class="status-tab-hint">{{ t.hint }}</span>
        </span>
        <span class="status-tab-count">{{ counts[t.key] == null ? "–" : counts[t.key] }}</span>
      </button>
    </div>

    <div class="surface">
      <div class="filter-bar">
        <v-text-field
          v-model="search"
          class="filter-bar-search"
          outlined
          dense
          hide-details
          clearable
          label="Application no., applicant, film or location"
          prepend-inner-icon="mdi-magnify"
          @input="onSearchInput"
          @keyup.enter="applyFilters"
        ></v-text-field>
        <v-autocomplete
          v-if="isAdmin"
          v-model="circleid"
          :items="circles"
          item-text="officeName"
          item-value="_id"
          outlined
          dense
          hide-details
          clearable
          label="Circle"
          prepend-inner-icon="mdi-compass-outline"
          no-data-text="No circle found"
          @change="onCircleChange"
        ></v-autocomplete>
        <v-autocomplete
          v-if="isAdmin"
          v-model="divisionid"
          :items="divisions"
          item-text="officeName"
          item-value="_id"
          outlined
          dense
          hide-details
          clearable
          label="Division"
          prepend-inner-icon="mdi-office-building-outline"
          no-data-text="No division found"
          @change="applyFilters"
        ></v-autocomplete>
        <v-menu v-model="fromMenu" :close-on-content-click="false" max-width="290">
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              :value="formatDate(fromDate)"
              outlined
              dense
              hide-details
              readonly
              clearable
              label="From date"
              prepend-inner-icon="mdi-calendar-start"
              v-bind="attrs"
              v-on="on"
              @click:clear="fromDate = null; toDate = null; applyFilters()"
            ></v-text-field>
          </template>
          <v-date-picker v-model="fromDate" :max="toDate || today" color="#15803d" @change="fromMenu = false; applyFilters()"></v-date-picker>
        </v-menu>
        <v-menu v-model="toMenu" :close-on-content-click="false" max-width="290">
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              :value="formatDate(toDate)"
              outlined
              dense
              hide-details
              readonly
              clearable
              label="To date"
              prepend-inner-icon="mdi-calendar-end"
              v-bind="attrs"
              v-on="on"
              @click:clear="toDate = null; applyFilters()"
            ></v-text-field>
          </template>
          <v-date-picker v-model="toDate" :min="fromDate" :max="today" color="#15803d" @change="toMenu = false; applyFilters()"></v-date-picker>
        </v-menu>
        <v-btn v-if="hasFilters" depressed class="btn-act btn-act--neutral filter-bar-button" @click="clearFilters">
          <v-icon left small>mdi-filter-remove-outline</v-icon>
          Clear filters
        </v-btn>
        <v-btn
          v-if="canBulkDelete"
          depressed
          class="btn-act filter-bar-button"
          :class="selectMode ? 'btn-act--primary' : 'btn-act--neutral'"
          @click="toggleSelectMode"
        >
          <v-icon left small>{{ selectMode ? "mdi-check" : "mdi-checkbox-multiple-outline" }}</v-icon>
          {{ selectMode ? "Done" : "Select" }}
        </v-btn>
      </div>

      <!-- Bulk selection bar (super admin) -->
      <transition name="bulk-bar">
        <div v-if="selectMode" class="bulk-bar" :class="{ 'bulk-bar--active': selectedCount > 0 }">
          <div class="bulk-bar-info">
            <v-icon small :color="selectedCount ? '#b91c1c' : '#64748b'">
              {{ selectedCount ? "mdi-checkbox-marked-circle-outline" : "mdi-gesture-tap" }}
            </v-icon>
            <span v-if="selectedCount" class="bulk-bar-count">
              {{ selectedCount }} application{{ selectedCount === 1 ? "" : "s" }} selected
            </span>
            <span v-else class="bulk-bar-hint">Tick the applications you want to delete.</span>
            <span v-if="selectedCount" class="bulk-bar-hint">Selection is kept when you change pages.</span>
          </div>
          <div class="bulk-bar-actions">
            <v-btn v-if="list.length" text small class="text-none font-weight-bold" color="#15803d" @click="togglePage(!pageAllSelected)">
              {{ pageAllSelected ? "Unselect this page" : "Select this page" }}
            </v-btn>
            <v-btn v-if="selectedCount" text small class="text-none font-weight-bold" color="#64748b" @click="clearSelection">Clear</v-btn>
            <v-btn depressed class="btn-act btn-act--sm btn-act--danger" :disabled="!selectedCount" @click="confirmDialog = true">
              <v-icon small left>mdi-delete-outline</v-icon>
              Delete{{ selectedCount ? ` (${selectedCount})` : "" }}
            </v-btn>
          </div>
        </div>
      </transition>

      <div v-if="loading" class="pa-4">
        <v-skeleton-loader type="table-row@5"></v-skeleton-loader>
      </div>

      <div v-else-if="!list.length" class="empty-state">
        <v-icon size="52">mdi-file-search-outline</v-icon>
        <div class="cell-strong">{{ hasFilters ? "No applications match these filters" : "No " + currentTab.empty + " yet" }}</div>
        <div>{{ hasFilters ? "Change the search, place or dates, or clear the filters." : "They will appear here as soon as there are any." }}</div>
      </div>

      <div v-else class="table-scroll">
        <v-simple-table class="app-table">
          <thead>
            <tr>
              <th v-if="selectMode" class="select-cell">
                <v-simple-checkbox
                  :value="pageAllSelected"
                  :indeterminate="pageSomeSelected && !pageAllSelected"
                  color="#15803d"
                  :ripple="false"
                  @input="togglePage"
                ></v-simple-checkbox>
              </th>
              <th>Application</th>
              <th>Applicant</th>
              <th>Shooting</th>
              <th v-if="isAdmin">Division</th>
              <th>Fees</th>
              <th>Status</th>
              <th class="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in list" :key="item._id" :class="{ 'row--selected': selectMode && isSelected(item) }">
              <td v-if="selectMode" data-label="Select" class="select-cell">
                <v-simple-checkbox :value="isSelected(item)" color="#b91c1c" :ripple="false" @input="toggleItem(item)"></v-simple-checkbox>
              </td>
              <td data-label="Application">
                <div>
                  <span class="app-no">#{{ item.applicationNo || "N/A" }}</span>
                  <div v-if="item.originalApplication && item.originalApplication.applicationNo" class="cell-muted">
                    <v-icon x-small>mdi-link-variant</v-icon>
                    Extension of #{{ item.originalApplication.applicationNo }}
                  </div>
                  <div class="cell-muted">{{ formatDate(item.created_at) }}</div>
                </div>
              </td>
              <td data-label="Applicant">
                <div>
                  <div class="cell-strong">{{ item.applicantName || "N/A" }}</div>
                  <div v-if="item.applicantMobileNo" class="cell-muted">{{ item.applicantMobileNo }}</div>
                  <div v-if="item.applicantDistrict" class="cell-muted">{{ item.applicantDistrict }}</div>
                </div>
              </td>
              <td data-label="Shooting">
                <div>
                  <div class="cell-strong">{{ filmName(item) }}</div>
                  <div class="cell-muted">{{ item.natureOfApplication }}</div>
                  <div v-if="shootingDates(item)" class="cell-muted">
                    <v-icon x-small>mdi-calendar-range</v-icon>
                    {{ shootingDates(item) }}
                  </div>
                  <span v-if="item.filmShootingDetails && item.filmShootingDetails.length" class="member-count mt-1">
                    <v-icon x-small>mdi-map-marker-multiple-outline</v-icon>
                    {{ item.filmShootingDetails.length }} location{{ item.filmShootingDetails.length > 1 ? "s" : "" }}
                  </span>
                </div>
              </td>
              <td v-if="isAdmin" data-label="Division">
                <span class="cell-strong">{{ divisionName(item) }}</span>
              </td>
              <td data-label="Fees">
                <div>
                  <div class="cell-strong">₹{{ money(item.shootingfee) }}</div>
                  <div v-if="item.installationfee" class="cell-muted">Installation ₹{{ money(item.installationfee) }}</div>
                  <div v-if="refundShown(item)" class="cell-muted">Refund ₹{{ money(refundShown(item)) }}</div>
                </div>
              </td>
              <td data-label="Status">
                <div class="status-stack">
                  <span class="status-pill" :class="'status-pill--' + statusOf(item).tone">{{ statusOf(item).label }}</span>
                  <span v-if="item.status === 'Active'" class="with-stage">{{ currentlyWithLabel(item) }}</span>
                </div>
              </td>
              <td class="app-table-actions text-right">
                <div class="row-actions">
                  <v-btn depressed small class="btn-act btn-act--sm btn-act--primary" @click="view(item)">
                    <v-icon left small>mdi-eye-outline</v-icon>
                    Open
                  </v-btn>
                  <v-btn depressed small class="btn-act btn-act--sm btn-act--info" @click="openTimeline(item)">
                    <v-icon left small>mdi-timeline-clock-outline</v-icon>
                    Timeline
                  </v-btn>
                  <v-btn
                    v-if="item.status === 'Rejected'"
                    depressed
                    small
                    class="btn-act btn-act--sm btn-act--doc"
                    :loading="downloading === item._id"
                    @click="downloadRejection(item)"
                  >
                    <v-icon left small>mdi-file-download-outline</v-icon>
                    Rejection order
                  </v-btn>
                </div>
              </td>
            </tr>
          </tbody>
        </v-simple-table>
      </div>

      <div v-if="pages > 1" class="pagination-bar">
        <v-pagination v-model="currentPage" :length="pages" :total-visible="7" color="#15803d"></v-pagination>
      </div>
    </div>

    <TimelineDialog v-model="timelineDialog" :application="selectedApp || {}" />

    <!-- Bulk delete confirmation -->
    <v-dialog v-model="confirmDialog" max-width="480" persistent>
      <v-card class="dialog-card">
        <div class="dialog-head dialog-head--danger">
          <div class="dialog-head-title">Delete {{ selectedCount === 1 ? "1 application" : selectedCount + " applications" }}</div>
          <div class="dialog-head-subtitle">This cannot be undone</div>
        </div>
        <div class="pa-5">
          Delete {{ selectedCount === 1 ? "1 application" : selectedCount + " applications" }} (<b>{{ selectedPreview }}</b>) completely, along with their
          forwarding history and payment records?
        </div>
        <v-card-actions class="px-5 pb-5">
          <v-spacer></v-spacer>
          <v-btn depressed class="btn-act btn-act--neutral" :disabled="deleting" @click="confirmDialog = false">Cancel</v-btn>
          <v-btn depressed class="btn-act btn-act--danger-solid" :loading="deleting" @click="bulkDelete">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";
import moment from "moment";
import PageHeader from "@/components/Common/PageHeader.vue";
import TimelineDialog from "@/components/Common/TimelineDialog.vue";
import { currentlyWithLabel, statusOf } from "@/components/Common/deskConfig";
import { canBulkDeleteApps } from "@/components/Common/navConfig";

const PAGE_SIZE = 10;
// The server accepts at most this many applications per bulk delete.
const BULK_DELETE_LIMIT = 200;

// One tab per status filter of the list API (`status`), same meanings as the
// dashboard's totals. The dashboard opens this page with ?status=<status>.
const TABS = [
  { key: "all", status: null, name: "All applications", hint: "Every status", empty: "applications", icon: "mdi-file-document-multiple-outline", color: "#15803d", soft: "#dcfce7" },
  { key: "pending", status: "Pending", name: "Submitted", hint: "Under review", empty: "submitted applications", icon: "mdi-clock-outline", color: "#d97706", soft: "#fef3c7" },
  { key: "approved", status: "Success", name: "Approved", hint: "Fees set or paid", empty: "approved applications", icon: "mdi-check-decagram-outline", color: "#15803d", soft: "#dcfce7" },
  { key: "rejected", status: "rejected", name: "Rejected", hint: "Turned down", empty: "rejected applications", icon: "mdi-close-octagon-outline", color: "#dc2626", soft: "#fee2e2" },
  { key: "cancelled", status: "Cancelled", name: "Cancelled", hint: "Withdrawn or cancelled", empty: "cancelled applications", icon: "mdi-cancel", color: "#6366f1", soft: "#e0e7ff" },
  { key: "extended", status: "extended", name: "Extensions", hint: "Extension requests", empty: "extension requests", icon: "mdi-calendar-expand-horizontal", color: "#0369a1", soft: "#e0f2fe" },
];

function tabForStatus(status) {
  const s = String(status || "").toLowerCase();
  const found = TABS.find((t) => t.status && t.status.toLowerCase() === s);
  return found ? found.key : "all";
}

// All applications of the state (admin) or of the DFO's own division, by
// status, with search, place and date filters.
export default {
  name: "AllApplications",
  components: { PageHeader, TimelineDialog },
  props: {
    scope: {
      type: String,
      default: "admin",
      validator: (v) => v === "admin" || v === "division",
    },
  },
  data() {
    return {
      tabs: TABS,
      tab: tabForStatus(this.$route.query.status),
      list: [],
      counts: {},
      loading: false,
      currentPage: 1,
      totalData: 0,
      pages: 0,
      search: "",
      searchTimer: null,
      circleid: null,
      divisionid: null,
      circles: [],
      divisions: [],
      fromDate: null,
      toDate: null,
      fromMenu: false,
      toMenu: false,
      today: moment().format("YYYY-MM-DD"),
      timelineDialog: false,
      selectedApp: null,
      downloading: null,
      listRequest: 0,
      countRequest: 0,
      msg: "",
      showSnackBar: false,
      // Bulk delete: selected applications by id -> application number (kept across pages).
      selectMode: false,
      selected: {},
      confirmDialog: false,
      deleting: false,
    };
  },
  computed: {
    isAdmin() {
      return this.scope === "admin";
    },
    subtitle() {
      return this.isAdmin
        ? "Film shooting applications across every circle and division."
        : "Film shooting applications in your division.";
    },
    currentTab() {
      return TABS.find((t) => t.key === this.tab) || TABS[0];
    },
    hasFilters() {
      return !!((this.search && this.search.trim()) || this.circleid || this.divisionid || this.fromDate || this.toDate);
    },
    // Every role with a full application list (admin, super admin, DFO) may delete.
    canBulkDelete() {
      return canBulkDeleteApps();
    },
    selectedCount() {
      return Object.keys(this.selected).length;
    },
    selectedPreview() {
      const numbers = Object.values(this.selected);
      const more = numbers.length > 5 ? ` and ${numbers.length - 5} more` : "";
      return numbers.slice(0, 5).map((n) => "#" + n).join(", ") + more;
    },
    pageSomeSelected() {
      return this.list.some((item) => this.isSelected(item));
    },
    pageAllSelected() {
      return this.list.length > 0 && this.list.every((item) => this.isSelected(item));
    },
  },
  watch: {
    currentPage() {
      this.getList();
    },
  },
  mounted() {
    this.refresh();
    if (this.isAdmin) {
      this.getCircles();
      this.getDivisions();
    }
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
  },
  methods: {
    statusOf,
    currentlyWithLabel,
    toast(text) {
      this.msg = text;
      this.showSnackBar = true;
    },
    refresh() {
      this.getCounts();
      this.getList();
    },
    selectTab(key) {
      if (key === this.tab) return;
      this.tab = key;
      this.clearSelection();
      const status = this.currentTab.status;
      const query = Object.assign({}, this.$route.query);
      if (status) query.status = status;
      else delete query.status;
      this.$router.replace({ query }).catch(() => {});
      this.goToFirstPage();
    },
    // Back to page 1 (the page watcher reloads), or reload if already there
    goToFirstPage() {
      if (this.currentPage !== 1) this.currentPage = 1;
      else this.getList();
    },
    applyFilters() {
      clearTimeout(this.searchTimer);
      this.clearSelection();
      this.getCounts();
      this.goToFirstPage();
    },
    onSearchInput() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(this.applyFilters, 400);
    },
    onCircleChange() {
      this.divisionid = null;
      this.getDivisions();
      this.applyFilters();
    },
    clearFilters() {
      this.search = "";
      this.circleid = null;
      this.divisionid = null;
      this.fromDate = null;
      this.toDate = null;
      if (this.isAdmin) this.getDivisions();
      this.applyFilters();
    },
    // Filters of the list API, without the status and page
    filterParams() {
      const search = (this.search || "").trim();
      return {
        search: search || undefined,
        circle: this.isAdmin && this.circleid ? this.circleid : undefined,
        division: this.isAdmin && this.divisionid ? this.divisionid : undefined,
        startDate: this.fromDate || undefined,
        endDate: this.toDate || undefined,
      };
    },
    fetchPage(params) {
      return axios({
        method: "GET",
        url: this.isAdmin ? "/admin/getall-applications" : "/division/getall-applications",
        headers: { token: localStorage.getItem("token") },
        params: Object.assign(this.filterParams(), params),
      });
    },
    getList() {
      const request = ++this.listRequest;
      this.loading = true;
      this.fetchPage({ status: this.currentTab.status || undefined, page: this.currentPage, limit: PAGE_SIZE })
        .then((response) => {
          if (request !== this.listRequest) return;
          this.loading = false;
          this.list = (response.data && response.data.data) || [];
          this.totalData = (response.data && response.data.totalLength) || 0;
          this.pages = Math.ceil(this.totalData / PAGE_SIZE);
        })
        .catch((err) => {
          if (request !== this.listRequest) return;
          this.loading = false;
          this.list = [];
          this.toast("Could not load the applications. Please try again.");
          console.log(err);
        });
    },
    // How many applications each tab holds under the current filters
    getCounts() {
      const request = ++this.countRequest;
      Promise.all(
        TABS.map((t) =>
          this.fetchPage({ status: t.status || undefined, page: 1, limit: 1 })
            .then((response) => (response.data && response.data.totalLength) || 0)
            .catch(() => null)
        )
      ).then((totals) => {
        if (request !== this.countRequest) return;
        const counts = {};
        TABS.forEach((t, i) => {
          counts[t.key] = totals[i];
        });
        this.counts = counts;
      });
    },
    getCircles() {
      axios({ method: "GET", url: "/get/circles", headers: { token: localStorage.getItem("token") } })
        .then((response) => {
          this.circles = (response.data && response.data.data) || [];
        })
        .catch((err) => console.log(err));
    },
    getDivisions() {
      const circle = this.circles.find((c) => c._id === this.circleid);
      axios({
        method: "POST",
        url: "/get/divsions",
        headers: { token: localStorage.getItem("token") },
        data: circle && circle.path ? { path: circle.path } : {},
      })
        .then((response) => {
          this.divisions = (response.data && response.data.data) || [];
        })
        .catch((err) => {
          this.divisions = [];
          console.log(err);
        });
    },
    // ---------- bulk select / delete ----------
    toggleSelectMode() {
      this.selectMode = !this.selectMode;
      if (!this.selectMode) this.clearSelection();
    },
    isSelected(item) {
      return Object.prototype.hasOwnProperty.call(this.selected, item._id);
    },
    toggleItem(item) {
      if (this.isSelected(item)) {
        this.$delete(this.selected, item._id);
      } else {
        this.$set(this.selected, item._id, item.applicationNo || item._id);
      }
    },
    togglePage(value) {
      this.list.forEach((item) => {
        if (value) this.$set(this.selected, item._id, item.applicationNo || item._id);
        else this.$delete(this.selected, item._id);
      });
    },
    clearSelection() {
      this.selected = {};
    },
    bulkDelete() {
      const ids = Object.keys(this.selected);
      if (!ids.length) return;
      if (ids.length > BULK_DELETE_LIMIT) {
        this.confirmDialog = false;
        this.toast(`You can delete at most ${BULK_DELETE_LIMIT} applications at a time.`);
        return;
      }
      this.deleting = true;
      axios
        .post("/superadmin/delete/applications", { appids: ids }, { headers: { token: localStorage.getItem("token") } })
        .then((response) => {
          this.deleting = false;
          const data = response.data || {};
          this.toast(data.msg || (data.status ? "Applications deleted." : "Could not delete the applications."));
          if (data.status) {
            this.confirmDialog = false;
            this.clearSelection();
            this.selectMode = false;
            // The current page may now be past the last one (the page watcher reloads)
            const lastPage = Math.max(1, Math.ceil((this.totalData - (data.deleted || 0)) / PAGE_SIZE));
            this.getCounts();
            if (this.currentPage > lastPage) this.currentPage = lastPage;
            else this.getList();
          }
        })
        .catch((err) => {
          this.deleting = false;
          this.toast("Couldn't delete the selected applications. Please try again.");
          console.log(err);
        });
    },
    view(item) {
      this.$router.push({ path: "/AdminFilmDetailedView", query: { id: item._id } });
    },
    openTimeline(item) {
      this.selectedApp = item;
      this.timelineDialog = true;
    },
    // Opens the DFO's rejection order (PDF) in a new tab
    downloadRejection(item) {
      const tab = window.open();
      this.downloading = item._id;
      axios
        .get("/rejectby/divsion/pdf", {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          params: { appid: item._id },
          responseType: "blob",
        })
        .then((response) => {
          this.downloading = null;
          const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
          if (tab) tab.location.href = url;
          else window.open(url, "_blank");
        })
        .catch((err) => {
          this.downloading = null;
          if (tab) tab.close();
          this.toast("Could not open the rejection order.");
          console.log(err);
        });
    },
    filmName(item) {
      const first = item.filmShootingDetails && item.filmShootingDetails[0];
      return (first && first.filmName) || "Untitled production";
    },
    // First to last shooting day across the locations
    shootingDates(item) {
      const days = (item.filmShootingDetails || []).reduce((all, d) => all.concat([d.fromDate, d.toDate].filter(Boolean)), []);
      if (!days.length) return "";
      const sorted = days.map((d) => moment(d)).filter((m) => m.isValid()).sort((a, b) => a - b);
      if (!sorted.length) return "";
      const first = sorted[0];
      const last = sorted[sorted.length - 1];
      return first.isSame(last, "day") ? first.format("D MMM YYYY") : first.format("D MMM") + " – " + last.format("D MMM YYYY");
    },
    divisionName(item) {
      return (item.divisionid && item.divisionid.officeName) || "—";
    },
    // Applications refunded before the amount was stored show what was paid.
    refundShown(item) {
      if (item.refundAmount) return item.refundAmount;
      return item.refundStatus === "Completed" ? (item.shootingfee || 0) + (item.installationfee || 0) : 0;
    },
    money(value) {
      return Number(value || 0).toLocaleString("en-IN");
    },
    formatDate(value) {
      return value ? moment(value).format("D MMM YYYY") : "";
    },
  },
};
</script>

<style scoped>
/* Status tabs (same look as the desk tabs of ApplicationList) */
.status-tabs {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}

.status-tab {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  padding: 14px 16px;
  border: 1px solid var(--line);
  border-radius: 16px;
  background: #ffffff;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
}

.status-tab:hover {
  border-color: var(--tone);
  transform: translateY(-1px);
}

.status-tab:focus-visible {
  outline: 2px solid var(--tone);
  outline-offset: 2px;
}

.status-tab--active {
  border-color: var(--tone);
  background: linear-gradient(135deg, var(--tone-soft) 0%, #ffffff 100%);
  box-shadow: 0 8px 22px rgba(15, 23, 42, 0.08);
}

.status-tab-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border-radius: 12px;
  background: var(--tone-soft);
}

.status-tab-icon .v-icon {
  color: var(--tone) !important;
}

.status-tab--active .status-tab-icon {
  background: var(--tone);
}

.status-tab--active .status-tab-icon .v-icon {
  color: #ffffff !important;
}

.status-tab-text {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
}

.status-tab-name {
  font-size: 14px;
  font-weight: 800;
  color: var(--ink);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.status-tab-hint {
  font-size: 12px;
  font-weight: 600;
  color: var(--ink-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.status-tab-count {
  min-width: 34px;
  padding: 4px 10px;
  border-radius: 999px;
  background: #f1f5f9;
  color: #334155;
  font-size: 14px;
  font-weight: 800;
  text-align: center;
}

.status-tab--active .status-tab-count {
  background: var(--tone);
  color: #ffffff;
}

/* Where an open application is now */
.with-stage {
  display: inline-block;
  padding: 1px 8px;
  border-radius: 6px;
  background: #f3faf5;
  font-size: 11.5px;
  font-weight: 600;
  color: #14532d;
}

/* ---------- Bulk select ---------- */
.bulk-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px 16px;
  margin: 0 16px 12px;
  padding: 10px 12px 10px 14px;
  border: 1px dashed #cbd5e1;
  border-radius: 12px;
  background: #f8fafc;
  transition: background 0.2s ease, border-color 0.2s ease;
}

.bulk-bar--active {
  border-style: solid;
  border-color: #fecaca;
  background: #fef2f2;
}

.bulk-bar-info {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 8px;
  min-width: 0;
}

.bulk-bar-count {
  font-size: 14px;
  font-weight: 800;
  color: #7f1d1d;
}

.bulk-bar-hint {
  font-size: 12px;
  color: #64748b;
}

.bulk-bar-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
}

.bulk-bar-enter-active,
.bulk-bar-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.bulk-bar-enter,
.bulk-bar-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

.app-table th.select-cell,
.app-table td.select-cell {
  width: 48px;
  padding-right: 0 !important;
}

.app-table tbody tr.row--selected,
.app-table tbody tr.row--selected:hover {
  background: #fef2f2 !important;
}

@media (max-width: 768px) {
  .app-table tbody tr.row--selected {
    border-color: #fca5a5;
  }

  .app-table td.select-cell {
    width: 100%;
    align-items: center;
  }
}

@media (max-width: 600px) {
  .bulk-bar {
    margin: 0 12px 12px;
  }

  .bulk-bar-actions {
    width: 100%;
  }

  .bulk-bar-actions .btn-act {
    flex: 1 1 auto;
  }
}

@media (max-width: 1500px) {
  .status-tabs {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 760px) {
  .status-tabs {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
  }

  .status-tab {
    gap: 8px;
    padding: 10px 12px;
    border-radius: 14px;
  }

  .status-tab-icon {
    width: 34px;
    height: 34px;
    border-radius: 10px;
  }

  .status-tab-hint {
    display: none;
  }

  .status-tab-count {
    min-width: 28px;
    padding: 2px 8px;
    font-size: 13px;
  }
}
</style>
